/**
 * ZONIY - Shopping Cart Context
 * @module CartContext
 * @author Affan - Mobile Developer
 * @description Shopping cart state management
 * @version 1.0.0
 * Git: Affan <affan@linesoft.com> - Cart implementation
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);

  const fetchCart = useCallback(async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/cart`);
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (user) {
      fetchCart();
    } else {
      setCart({ items: [], total: 0 });
    }
  }, [user, fetchCart]);

  const addToCart = async (partId, quantity = 1) => {
    if (!token) {
      toast.error('Please login to add items to cart');
      return false;
    }
    
    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/api/cart/add`, {
        part_id: partId,
        quantity
      });
      setCart(response.data);
      toast.success('Added to cart');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to cart');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (partId) => {
    try {
      setLoading(true);
      const response = await axios.delete(`${API_URL}/api/cart/remove/${partId}`);
      setCart(response.data);
      toast.success('Removed from cart');
    } catch (error) {
      toast.error('Failed to remove from cart');
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (partId, quantity) => {
    try {
      setLoading(true);
      const response = await axios.put(`${API_URL}/api/cart/update`, {
        part_id: partId,
        quantity
      });
      setCart(response.data);
    } catch (error) {
      toast.error('Failed to update cart');
    } finally {
      setLoading(false);
    }
  };

  const clearCart = () => {
    setCart({ items: [], total: 0 });
  };

  const value = {
    cart,
    loading,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    fetchCart,
    itemCount: cart.items?.length || 0
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
